import React, { useState } from 'react';
import { FeedbackButton, type FeedbackContext } from './feedback-button';
import { FeedbackPopover } from './feedback-popover';
import { useFeedback } from '@/hooks/useFeedback';
import { canSubmitFeedback, isContextSafeForFeedback, FeedbackQueue } from '@/utils/feedbackUtils';
import { cn } from '@/lib/utils';

interface ContextAwareFeedbackWidgetProps {
  className?: string;
  position?: 'fixed' | 'relative';
  context?: FeedbackContext; // Optional override
  disabled?: boolean;
}

/**
 * Context-aware feedback widget that automatically detects context and handles submission
 * Implements requirements 1.1, 2.4, 2.5, 6.2, 6.3, 7.1, 7.2
 */
export function ContextAwareFeedbackWidget({
  className,
  position = 'fixed',
  context: contextOverride,
  disabled = false
}: ContextAwareFeedbackWidgetProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { submitFeedback, isSubmitting, getContextInfo } = useFeedback();

  const contextInfo = getContextInfo();
  const context = contextOverride || contextInfo.context;
  const sessionId = contextInfo.sessionId;

  const handleFeedbackSubmit = async (feedback: string) => {
    try {
      // Check if it's safe to submit feedback without disrupting workflow
      if (!canSubmitFeedback() || !isContextSafeForFeedback()) {
        // Queue the submission for later if context is not safe
        await FeedbackQueue.add(() => submitFeedback(feedback));
      } else {
        // Submit immediately if context is safe
        await submitFeedback(feedback);
      }
      
      setIsPopoverOpen(false);
    } catch (error) {
      // Error is already handled by the hook (toast shown)
      throw error;
    }
  };

  const handleOpenPopover = () => {
    if (!disabled && isContextSafeForFeedback()) {
      setIsPopoverOpen(true);
    }
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
  };

  return (
    <>
      <FeedbackButton
        className={className}
        position={position}
        context={context}
        sessionId={sessionId}
        onClick={handleOpenPopover}
        disabled={disabled || isSubmitting}
      />
      
      <FeedbackPopover
        isOpen={isPopoverOpen}
        onClose={handleClosePopover}
        onSubmit={handleFeedbackSubmit}
        context={context}
        sessionId={sessionId}
      />
    </>
  );
}

/**
 * Fixed position feedback widget for annotation screens
 */
export function FixedFeedbackWidget(props: Omit<ContextAwareFeedbackWidgetProps, 'position'>) {
  return (
    <ContextAwareFeedbackWidget
      {...props}
      position="fixed"
      className={cn("bottom-4 right-4 md:bottom-6 md:right-6", props.className)}
    />
  );
}

/**
 * Inline feedback widget for embedding in components
 */
export function InlineFeedbackWidget(props: Omit<ContextAwareFeedbackWidgetProps, 'position'>) {
  return (
    <ContextAwareFeedbackWidget
      {...props}
      position="relative"
    />
  );
}